import { useAppSelector } from 'appState/store'
import { NavLinkWithTooltip } from 'components/Layout'
import { TopNav } from 'components/Navs'
import {
  getAwaitingEOBBatchCount,
  getBatchCount,
  getBatchPendingCount,
  getBatchProcessedCount,
} from 'features/autocheck/autoCheckApi'
import { AutoCheckPageHeader } from 'features/autocheck/components/AutoCheckHeader'
import { IS_NEW_USER } from 'features/autocheck/components/UploadClaimModal/Constants'
import { WelcomeAutoCheck } from 'features/autocheck/components/WelcomeAutoCheckModal'
import { getUserPreference, settingsServiceApiPaths } from 'features/settings/settingsApi'
import { useUserProfile } from 'features/settings/settingsHooks'
import { IUserRoleGuidEnum, UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { PathsEnum } from 'pages/Paths'
import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Badge } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ColorsEnum } from 'types/cssTypes'

import { AutoChecKUpload } from './AutoCheckUpload'

const AutoCheck = () => {
  const { root, refresh } = useAppSelector((state) => state.batch)
  const { user } = useAppSelector((state) => state.auth)
  const { data: profile } = useUserProfile(user?.id)
  const roleIds = profile?.roles.map((role) => role.roleId)
  const isbatchUploadUser = roleIds?.includes(IUserRoleGuidEnum.batchUploadPermission)
  const isrunAutocheckUser = roleIds?.includes(IUserRoleGuidEnum.runAutoCheckPermission)

  const [processingCount, setProcessingCount] = useState(0)
  const [processedCount, setProcessedCount] = useState(0)
  const [awaitingEOBCount, setAwaitingEOBCount] = useState(0)
  const [pendingCount, setPendingCount] = useState(0)
  const [isNewUser, setNewUser] = useState(false)

  const location = useLocation()
  const filterColorMapping: { [key: string]: keyof typeof ColorsEnum } = {
    'batch-upload': ColorsEnum.tertiary,
    'batch-proccesing': ColorsEnum.grey,
    'batch-processed': ColorsEnum.success,
    'batch-archive': ColorsEnum.info,
    'batch-awaiting': ColorsEnum.warning,
    'batch-pending': ColorsEnum.info,
    'eob-review': ColorsEnum.warning,
    'unsupported-fax': ColorsEnum.warning,
  }
  const pathSliceViewMapping = {
    'batch-upload': PathsEnum.batchUpload,
    'batch-proccesing': PathsEnum.batchProcessing,
    'batch-processed': PathsEnum.batchProcessed,
    'batch-archive': PathsEnum.batchArchive,
    'batch-awaiting': PathsEnum.batchAwaiting,
    'batch-pending': PathsEnum.batchPending,
    'eob-review': PathsEnum.eobReview,
    'unsupported-fax': PathsEnum.unSupportedFax,
  }
  const currentTab = location.pathname.split('/').pop() as keyof typeof pathSliceViewMapping

  const tooltipsOverlay = {
    [PathsEnum.batchUpload]:
      'Import claims in a CSV file for the AI Agent to independently begin checking claim status.Claims that still need to be worked to attain payer status',
    [PathsEnum.batchProcessing]: 'View the progress of claim files in AutoCheck claim processing.',
    [PathsEnum.batchProcessed]: 'View completed claims in AutoCheck batch',
    [PathsEnum.batchArchive]: 'View archived claims in AutoCheck batch',
    [PathsEnum.batchAwaiting]: 'View claims awaiting EOB',
    [PathsEnum.batchPending]: 'View claims that have not been processed',
    [PathsEnum.eobReview]:
      'We’ve received EOBs that could not be matched to specific claims. You can download and review them below.',
    [PathsEnum.unSupportedFax]: 'Unsupported Faxes',
  }

  useEffect(() => {
    const isUserLoggedInAlready = sessionStorage.getItem(IS_NEW_USER)
    if (!isUserLoggedInAlready) {
      getUserPreference(settingsServiceApiPaths.userPreferencesGet(UserPreferencesKeysEnum.isNewUser)).then(
        (newUser) => {
          if (newUser?.value !== 'false') {
            sessionStorage.setItem(IS_NEW_USER, newUser)
            setNewUser(true)
          }
        },
      )
    }
  }, [])

  useEffect(() => {
    getBatchProcessedCount().then((res) => {
      setProcessedCount(res.count)
    })
    getBatchPendingCount().then((res) => {
      setPendingCount(res.count)
    })
    getBatchCount().then((res) => {
      setProcessingCount(res.count)
    })
    getAwaitingEOBBatchCount().then((res) => {
      setAwaitingEOBCount(res.count)
    })
  }, [root, refresh])

  return (
    <Div>
      <AutoCheckPageHeader isrunAutocheckUser={isrunAutocheckUser!} currentTab={currentTab} />
      <div className="sidebar-container">
        <AutoChecKUpload />
      </div>
      {isNewUser ? (
        <WelcomeAutoCheck showModal={isNewUser} setWelcomeModal={() => setNewUser(false)} />
      ) : (
        <div className="tabs-nav-container">
          <div className="table-header">
            {isbatchUploadUser && (
              <TopNav activeLinkThemeColor={filterColorMapping[currentTab]}>
                <NavLinkWithTooltip to={PathsEnum.batchPending} overlay={tooltipsOverlay[PathsEnum.batchPending]}>
                  <div className="d-flex align-items-center justify-content-center gap-2 p-relative">
                    <span className="icon-title-space">Pending</span>
                    <Badge variant="badge" type="info">
                      {pendingCount}
                    </Badge>
                  </div>
                </NavLinkWithTooltip>

                <NavLinkWithTooltip to={PathsEnum.batchProcessing} overlay={tooltipsOverlay[PathsEnum.batchProcessing]}>
                  <div className="d-flex align-items-center justify-content-center gap-2 p-relative">
                    <span className="icon-title-space">Processing</span>
                    <Badge variant="badge" type="default">
                      {processingCount}
                    </Badge>
                  </div>
                </NavLinkWithTooltip>
                <NavLinkWithTooltip
                  to={PathsEnum.batchAwaiting}
                  overlay={tooltipsOverlay[PathsEnum.batchAwaiting]}
                  tooltipTransform={{ x: 'translateX(-35%)' }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2 p-relative">
                    <span className="icon-title"> Awaiting EOBs</span>
                    <Badge variant="badge" type="warning">
                      {awaitingEOBCount}
                    </Badge>
                  </div>
                </NavLinkWithTooltip>
                <NavLinkWithTooltip to={PathsEnum.batchProcessed} overlay={tooltipsOverlay[PathsEnum.batchProcessed]}>
                  <div className="d-flex align-items-center justify-content-center gap-2 p-relative">
                    <span className="icon-title"> Finished</span>
                    <Badge variant="badge" type="success">
                      {processedCount}
                    </Badge>
                  </div>
                </NavLinkWithTooltip>
                <NavLinkWithTooltip to={PathsEnum.eobReview} overlay={tooltipsOverlay[PathsEnum.eobReview]}>
                  <div className="d-flex align-items-center justify-content-center gap-2 p-relative">
                    <span className="icon-title-space"> EOBs for Review</span>
                  </div>
                </NavLinkWithTooltip>
                {/* {isOutboundUser && (
                  <NavLinkWithTooltip to={PathsEnum.unSupportedFax} overlay={tooltipsOverlay[PathsEnum.unSupportedFax]}>
                    <div className="d-flex align-items-center justify-content-center gap-2 p-relative">
                      <span className="icon-title-space"> Unsupported Faxes</span>
                    </div>
                  </NavLinkWithTooltip>
                )} */}
              </TopNav>
            )}
            {isbatchUploadUser && (
              <div className="archive">
                <TopNav activeLinkThemeColor={filterColorMapping[currentTab]}>
                  <NavLinkWithTooltip
                    to={PathsEnum.batchArchive}
                    overlay={tooltipsOverlay[PathsEnum.batchArchive]}
                    arrowPosition={87}
                    tooltipTransform={{ x: 'translateX(-75%)' }}
                  >
                    <div className="d-flex align-items-center justify-content-center gap-2 p-relative">
                      <span> Archived</span>
                    </div>
                  </NavLinkWithTooltip>
                </TopNav>
              </div>
            )}
          </div>
          <Outlet />
        </div>
      )}
    </Div>
  )
}

const Div = styled.div`
  height: calc(100vh - ${({ theme }) => theme.constants.navbarHeight});
  position: relative;
  span {
    .icon {
      svg {
        color: ${({ theme }) => theme.colors.grey.neutral};
        width: ${({ theme }) => theme.util.pixelToRem(18)};
        height: ${({ theme }) => theme.util.pixelToRem(18)};
      }
    }
  }

  .archive {
    margin-right: ${({ theme }) => theme.spacing.l};
    ${({ theme }) => theme.typography.m.medium};
    color: ${({ theme }) => theme.colors.grey.dark};
    cursor: pointer;
  }

  .archive:hover {
    margin-right: ${({ theme }) => theme.spacing.l};
    ${({ theme }) => theme.typography.m.medium};
    color: ${({ theme }) => theme.colors.grey.neutral};
    cursor: pointer;
  }

  .sidebar-container {
    float: left;
  }

  .table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    border-bottom: ${({ theme }) => theme.util.pixelToRem(1)} solid ${({ theme }) => theme.colors.primary.lighter};
    z-index: ${({ theme }) => theme.zIndex.sticky};

    .table-header-right {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-family: ${({ theme }) => theme.fontFamily.milliard};
      font-style: normal;
      ${({ theme }) => theme.typography.s.normal};
      text-align: right;
      color: ${({ theme }) => theme.colors.primary.darker};
    }
  }
  .icon-title {
    padding-top: 0.125rem;
  }
  .icon-title-space {
    padding-bottom: 2px;
  }
`

export { AutoCheck }
